import { useMemo } from 'react';
import { ReadOnlyItem, Row, Text, YColumnsType } from '@ydistri/ds';
import { DisplayEntityIdType, SkuRedistributionResponse } from '@ydistri/api-sdk';
import { useColumnWidth } from '../../../../../../../hooks/useColumnWidth';
import { useApplicationData } from '../../../../../../../hooks/useApplicationData';
import { useApplicationConfiguration } from '../../../../../../../hooks/useApplicationConfiguration';
import {
  EntityColumns,
  EntityColumnsConfig,
  useProductTableColumnsProvider,
  useStoreTableColumnsProvider,
} from '../../../../../../../hooks/useEntityTableColumnsProvider';
import { InfoIconTooltip } from '../../../../../../../components/icons/InfoIconTooltip';
import { formatNumber } from '@ydistri/generic-utils';

export const useSkuRedistributionTableColumns = (): YColumnsType<SkuRedistributionResponse>[] => {
  const { productIdColumnWidth } = useColumnWidth();
  const { departments: hasDepartments, brands: hasBrands } = useApplicationData();
  const { displayStoreId } = useApplicationConfiguration();

  const storeColumnProvider = useStoreTableColumnsProvider();
  const productColumnProvider = useProductTableColumnsProvider();

  return useMemo(() => {
    // store name is wider if neither Id nor Code are displayed
    const storeNameWidth = displayStoreId === DisplayEntityIdType.None ? '22rem' : '17rem';

    const sourceStoreColumnsConfig: EntityColumnsConfig<SkuRedistributionResponse> = {
      [EntityColumns.NAME]: {
        title: 'Source Store [from]',
        width: storeNameWidth,
        apiFilterable: true,
      },
      [EntityColumns.ID]: {
        width: '8rem',
        apiFilterable: true,
      },
      [EntityColumns.CODE]: {
        apiFilterable: true,
        width: '8rem',
      },
    };
    const sourceStoreColumns = storeColumnProvider<SkuRedistributionResponse>(
      sourceStoreColumnsConfig,
      'sourceStore',
      'Source Store',
    );

    const targetStoreColumnsConfig: EntityColumnsConfig<SkuRedistributionResponse> = {
      [EntityColumns.NAME]: {
        title: 'Target Store [to]',
        width: storeNameWidth,
        apiFilterable: true,
      },
      [EntityColumns.ID]: {
        width: '8rem',
        apiFilterable: true,
      },
      [EntityColumns.CODE]: {
        apiFilterable: true,
        width: '8rem',
      },
    };
    const targetStoreColumns = storeColumnProvider<SkuRedistributionResponse>(
      targetStoreColumnsConfig,
      'targetStore',
      'Target Store',
    );

    const productColumnsConfig: EntityColumnsConfig<SkuRedistributionResponse> = {
      [EntityColumns.ID]: {
        apiFilterable: true,
        width: productIdColumnWidth,
      },
      [EntityColumns.CODE]: {
        apiFilterable: true,
        width: productIdColumnWidth,
      },
      [EntityColumns.NAME]: {
        apiFilterable: true,
      },
    };
    const productColumns = productColumnProvider(productColumnsConfig);

    const result: YColumnsType<SkuRedistributionResponse>[] = [...productColumns];

    if (hasBrands) {
      result.push({
        title: 'Brand',
        key: 'brandName',
        dataIndex: ['brand', 'name'],
        sorter: true,
        width: '10rem',
        apiColumnName: 'Brand/Name',
        apiFilterable: true,
        apiFilterType: 'text',
      });
    }

    if (hasDepartments) {
      result.push({
        title: 'Department',
        key: 'departmentName',
        dataIndex: ['department', 'name'],
        sorter: true,
        // width: '8rem',
        apiColumnName: 'Department/Name',
        apiFilterable: true,
        apiFilterType: 'text',
      });
    }
    result.push(...sourceStoreColumns);
    result.push(...targetStoreColumns);

    result.push({
      title: 'Qty',
      key: 'quantity',
      dataIndex: 'quantity',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: '6rem',
      apiColumnName: 'Quantity',
      apiFilterable: true,
      apiFilterType: 'number',
      render: (value: number, record) =>
        record.description && record.description.length > 0 ? (
          <Row $gap="8px" $justifyContent="flex-end">
            <Text>{formatNumber(value, 0)}</Text>
            <InfoIconTooltip
              content={<ReadOnlyItem label="Picking instruction" value={record.description} />}
            />
          </Row>
        ) : (
          formatNumber(value, 0)
        ),
    });

    result.push({
      title: 'Value',
      key: 'value',
      dataIndex: 'value',
      align: 'right',
      width: '8rem',
      render: (value: number) => formatNumber(value, 2),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'descend',
      apiColumnName: 'Value',
      apiFilterable: true,
      apiFilterType: 'number',
    });

    result.push({
      title: 'Source SKU Type',
      key: 'sourceSkuType',
      dataIndex: ['sourceSku', 'skuTypeId'],
      width: '10rem',
      sorter: true,
      apiColumnName: 'SourceSku/SkuTypeId',
      apiFilterable: true,
      apiFilterType: 'options',
      filters: [
        {
          text: 'DeadStock',
          value: 'DeadStock',
        },
        {
          text: 'SlowMover',
          value: 'SlowMover',
        },
        {
          text: 'FastMover',
          value: 'FastMover',
        },
      ],
    });

    return result;
  }, [
    displayStoreId,
    storeColumnProvider,
    productIdColumnWidth,
    productColumnProvider,
    hasBrands,
    hasDepartments,
  ]);
};
